// src/pages/BusinessPage.jsx

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Input,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";

import {
  FiBarChart2,
  FiCheck,
  FiCreditCard,
  FiGift,
  FiMap,
  FiMapPin,
  FiPackage,
  FiRepeat,
  FiSend,
  FiSmartphone,
  FiUsers,
} from "react-icons/fi";

import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";

const MotionBox = motion(Box);

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

// -------------------------------------------------------
// BUSINESS PAGE
// -------------------------------------------------------

export default function BusinessPage() {
  const navigate = useNavigate();

  const [households, setHouseholds] = useState("");

  const estimatedPrice = useMemo(() => {
    const count = Number.parseInt(households, 10);

    if (!Number.isFinite(count) || count <= 0) {
      return "0.00";
    }

    return (count * 0.01).toFixed(2);
  }, [households]);

  const handleHouseholdChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    setHouseholds(value);
  };

  return (
    <Box bg="white">
      {/* =====================================================
          SEO
      ===================================================== */}

      <Helmet>
        <title>
          Place Coupons by Postcode Sector | Postcode.Coupons
        </title>

        <meta
          name="description"
          content="Place your coupon in postcode sectors for 1p per household per sector. Printed campaigns are delivered to households through Royal Mail."
        />

        <link
          rel="canonical"
          href="https://postcode.coupons/coupon-ads"
        />

        <meta
          property="og:title"
          content="Place Coupons by Postcode Sector"
        />

        <meta
          property="og:description"
          content="Place your coupon in postcode sectors for 1p per household per sector with Postcode.Coupons."
        />

        <meta
          property="og:image"
          content="https://postcode.coupons/og-business.png"
        />

        <meta
          property="og:url"
          content="https://postcode.coupons/coupon-ads"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Postcode.Coupons"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Place Coupons by Postcode Sector"
        />

        <meta
          name="twitter:description"
          content="Coupon placement from 1p per household per postcode sector."
        />

        <meta
          name="twitter:image"
          content="https://postcode.coupons/og-business.png"
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Postcode.Coupons Coupon Placement",
            description:
              "Coupon placement in postcode sectors for 1p per household per sector.",
            provider: {
              "@type": "Organization",
              name: "POSTCODE.COUPONS LIMITED",
              url: "https://postcode.coupons/",
            },
            areaServed: {
              "@type": "Country",
              name: "United Kingdom",
            },
            offers: {
              "@type": "Offer",
              priceCurrency: "GBP",
              description:
                "1p per household per postcode sector",
              url: "https://postcode.coupons/coupon-ads",
            },
          })}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
      ===================================================== */}

      <Box
        as="section"
        pt={{
          base: 10,
          md: 16,
        }}
        pb={{
          base: 10,
          md: 16,
        }}
        bg="white"
        borderBottomWidth="1px"
        borderColor="gray.100"
      >
        <Container maxW="container.lg">
          <MotionBox
            initial={{
              opacity: 0,
              y: 14,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.35,
            }}
          >
            <VStack
              spacing={5}
              maxW="800px"
              mx="auto"
              textAlign="center"
            >
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg={BRAND_RED_LIGHT}
                color={BRAND_RED_DARK}
                fontSize="xs"
                fontWeight="extrabold"
                letterSpacing="wide"
              >
                COUPONS BY POSTCODE SECTOR
              </Badge>

              <Heading
                as="h1"
                color="gray.900"
                fontSize={{
                  base: "3xl",
                  md: "5xl",
                }}
                lineHeight="1.08"
                letterSpacing="-0.03em"
                fontWeight="extrabold"
              >
                Put your coupon on the map
              </Heading>

              <Text
                maxW="700px"
                color="gray.700"
                fontSize={{
                  base: "md",
                  md: "xl",
                }}
                lineHeight="1.7"
              >
                Place your coupon in selected postcode sectors for{" "}
                <strong>
                  1p per household per sector
                </strong>
                .
              </Text>

              <Text
                maxW="650px"
                color="gray.600"
                fontSize={{
                  base: "sm",
                  md: "md",
                }}
                lineHeight="1.7"
              >
                Your coupon appears in the postcode-sector campaign,
                online through Postcode.Coupons and on its own QR
                redemption page.
              </Text>

              <Flex
                direction={{
                  base: "column",
                  sm: "row",
                }}
                justify="center"
                width={{
                  base: "100%",
                  sm: "auto",
                }}
                gap={3}
                pt={2}
              >
                <Button
                  size="lg"
                  width={{
                    base: "100%",
                    sm: "auto",
                  }}
                  px={8}
                  bg={BRAND_RED}
                  color="white"
                  borderRadius="lg"
                  fontWeight="extrabold"
                  _hover={{
                    bg: BRAND_RED_DARK,
                  }}
                  onClick={() =>
                    navigate("/signup?business=true")
                  }
                >
                  Place Your Coupon
                </Button>

                <Button
                  size="lg"
                  width={{
                    base: "100%",
                    sm: "auto",
                  }}
                  px={8}
                  variant="outline"
                  borderWidth="2px"
                  borderColor={BRAND_RED}
                  color={BRAND_RED_DARK}
                  borderRadius="lg"
                  fontWeight="bold"
                  leftIcon={<FiMap />}
                  _hover={{
                    bg: BRAND_RED_LIGHT,
                  }}
                  onClick={() => navigate("/map")}
                >
                  View Postcode Map
                </Button>
              </Flex>

              <Text
                color="gray.500"
                fontSize="xs"
                lineHeight="1.6"
              >
                Campaigns proceed to printing after the required
                campaign capacity has been filled.
              </Text>
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      {/* =====================================================
          CORE VALUE
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg={BRAND_RED_LIGHT}
        borderBottomWidth="1px"
        borderColor={BRAND_BORDER}
      >
        <Container maxW="container.lg">
          <Box
            maxW="720px"
            mx="auto"
            mb={{
              base: 7,
              md: 10,
            }}
            textAlign="center"
          >
            <Heading
              color="gray.900"
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              One coupon. Three connected channels.
            </Heading>

            <Text
              mt={3}
              color="gray.600"
              lineHeight="1.7"
            >
              Every coupon connects the printed campaign, the
              postcode.coupons platform and a QR redemption journey.
            </Text>
          </Box>

          <SimpleGrid
            columns={{
              base: 1,
              md: 3,
            }}
            spacing={5}
          >
            <FeatureCard
              icon={FiPackage}
              title="Printed coupon"
            >
              Your coupon is included in the campaign produced for
              the selected postcode sector.
            </FeatureCard>

            <FeatureCard
              icon={FiMapPin}
              title="Postcode presence"
            >
              Your coupon can also be viewed through its postcode
              sector on the Postcode.Coupons platform.
            </FeatureCard>

            <FeatureCard
              icon={FiSmartphone}
              title="QR redemption page"
            >
              Your QR code opens a dedicated page containing your
              coupon and redemption details.
            </FeatureCard>
          </SimpleGrid>
        </Container>
      </Box>

      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg="white"
      >
        <Container maxW="container.lg">
          <Box
            maxW="720px"
            mx="auto"
            mb={{
              base: 7,
              md: 10,
            }}
            textAlign="center"
          >
            <Heading
              color="gray.900"
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              How coupon placement works
            </Heading>

            <Text
              mt={3}
              color="gray.600"
              lineHeight="1.7"
            >
              Select the sector, prepare the coupon and join the
              campaign.
            </Text>
          </Box>

          <SimpleGrid
            columns={{
              base: 1,
              sm: 2,
              lg: 4,
            }}
            spacing={4}
          >
            <ProcessCard
              number="01"
              icon={FiMap}
              title="Choose a sector"
            >
              Select the postcode sector where you want your coupon
              placed.
            </ProcessCard>

            <ProcessCard
              number="02"
              icon={FiCreditCard}
              title="Book your position"
            >
              The price is calculated from the number of households
              in that sector.
            </ProcessCard>

            <ProcessCard
              number="03"
              icon={FiPackage}
              title="Campaign fills"
            >
              The postcode-sector campaign is filled to its required
              capacity.
            </ProcessCard>

            <ProcessCard
              number="04"
              icon={FiSend}
              title="Print and delivery"
            >
              The completed campaign is scheduled for printing and
              household delivery.
            </ProcessCard>
          </SimpleGrid>
        </Container>
      </Box>

      {/* =====================================================
          PRICING CALCULATOR
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg={BRAND_RED}
        color="white"
      >
        <Container maxW="container.md">
          <VStack
            spacing={5}
            textAlign="center"
          >
            <Heading
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              Calculate a coupon placement
            </Heading>

            <Text
              maxW="620px"
              opacity={0.94}
              lineHeight="1.7"
            >
              Enter a household count to see the coupon-placement
              price at 1p per household.
            </Text>

            <Box
              width="100%"
              maxW="520px"
              p={{
                base: 5,
                md: 7,
              }}
              bg="white"
              color="gray.900"
              borderRadius="2xl"
              boxShadow="xl"
            >
              <Text
                mb={2}
                color="gray.700"
                fontSize="sm"
                fontWeight="bold"
                textAlign="left"
              >
                Number of households
              </Text>

              <Input
                type="text"
                inputMode="numeric"
                value={households}
                onChange={handleHouseholdChange}
                placeholder="For example, 7200"
                size="lg"
                bg="white"
                borderColor="gray.300"
                _focus={{
                  borderColor: BRAND_RED,
                  boxShadow: `0 0 0 1px ${BRAND_RED}`,
                }}
              />

              <Box
                mt={5}
                p={5}
                bg={BRAND_RED_LIGHT}
                borderWidth="1px"
                borderColor={BRAND_BORDER}
                borderRadius="xl"
              >
                <Text
                  color="gray.600"
                  fontSize="sm"
                >
                  Estimated coupon-placement price
                </Text>

                <Text
                  mt={1}
                  color={BRAND_RED_DARK}
                  fontSize={{
                    base: "3xl",
                    md: "4xl",
                  }}
                  fontWeight="extrabold"
                >
                  £{estimatedPrice}
                </Text>

                <Text
                  mt={1}
                  color="gray.500"
                  fontSize="xs"
                >
                  Based on 1p per household
                </Text>
              </Box>

              <Button
                mt={5}
                width="100%"
                size="lg"
                bg={BRAND_RED}
                color="white"
                fontWeight="extrabold"
                _hover={{
                  bg: BRAND_RED_DARK,
                }}
                onClick={() => navigate("/map")}
              >
                Find a Postcode Sector
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* =====================================================
          REDEMPTION PAGE
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg="white"
      >
        <Container maxW="container.lg">
          <SimpleGrid
            columns={{
              base: 1,
              md: 2,
            }}
            spacing={{
              base: 8,
              md: 12,
            }}
            alignItems="center"
          >
            <Box>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg={BRAND_RED_LIGHT}
                color={BRAND_RED_DARK}
                fontSize="xs"
                fontWeight="extrabold"
              >
                INCLUDED WITH YOUR COUPON
              </Badge>

              <Heading
                mt={4}
                color="gray.900"
                size={{
                  base: "lg",
                  md: "xl",
                }}
              >
                Your own coupon redemption page
              </Heading>

              <Text
                mt={4}
                color="gray.600"
                lineHeight="1.8"
              >
                Each coupon can open a dedicated redemption page
                containing the coupon, business information,
                redemption instructions and QR journey.
              </Text>

              <List
                mt={5}
                spacing={3}
                color="gray.700"
              >
                <Feature>
                  Dedicated coupon page
                </Feature>

                <Feature>
                  Unique QR journey
                </Feature>

                <Feature>
                  Business information and coupon terms
                </Feature>

                <Feature>
                  Mobile-friendly redemption experience
                </Feature>

                <Feature>
                  Shareable coupon link
                </Feature>
              </List>
            </Box>

            <Box
              p={{
                base: 5,
                md: 7,
              }}
              bg={BRAND_RED_LIGHT}
              borderWidth="1px"
              borderColor={BRAND_BORDER}
              borderRadius="2xl"
              boxShadow="md"
            >
              <Box
                maxW="330px"
                mx="auto"
                overflow="hidden"
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor={BRAND_BORDER}
                boxShadow="sm"
              >
                <Box
                  px={5}
                  py={4}
                  bg={BRAND_RED}
                  color="white"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="extrabold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Your coupon
                  </Text>

                  <Heading
                    mt={2}
                    size="md"
                  >
                    Coupon title
                  </Heading>
                </Box>

                <VStack
                  align="stretch"
                  spacing={4}
                  p={5}
                >
                  <Text
                    color="gray.700"
                    fontSize="sm"
                    lineHeight="1.7"
                  >
                    Your coupon details and redemption terms appear
                    here.
                  </Text>

                  <Flex
                    align="center"
                    justify="center"
                    height="120px"
                    bg="gray.50"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="gray.200"
                    color={BRAND_RED}
                    fontSize="3xl"
                  >
                    <FiSmartphone />
                  </Flex>

                  <Button
                    bg={BRAND_RED}
                    color="white"
                    _hover={{
                      bg: BRAND_RED_DARK,
                    }}
                    pointerEvents="none"
                  >
                    Redeem Coupon
                  </Button>
                </VStack>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* =====================================================
          PRO ANALYTICS
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg={BRAND_RED_LIGHT}
        borderTopWidth="1px"
        borderBottomWidth="1px"
        borderColor={BRAND_BORDER}
      >
        <Container maxW="container.lg">
          <SimpleGrid
            columns={{
              base: 1,
              md: 2,
            }}
            spacing={{
              base: 8,
              md: 12,
            }}
            alignItems="center"
          >
            <Box
              p={{
                base: 5,
                md: 7,
              }}
              bg="white"
              borderWidth="1px"
              borderColor={BRAND_BORDER}
              borderRadius="2xl"
              boxShadow="md"
            >
              <Flex
                align="center"
                gap={3}
                mb={6}
              >
                <Flex
                  width="46px"
                  height="46px"
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg={BRAND_RED_LIGHT}
                  color={BRAND_RED}
                  fontSize="xl"
                >
                  <FiBarChart2 />
                </Flex>

                <Box>
                  <Text
                    color={BRAND_RED_DARK}
                    fontSize="xs"
                    fontWeight="extrabold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    QR Analytics Pro
                  </Text>

                  <Text
                    color="gray.900"
                    fontSize="xl"
                    fontWeight="extrabold"
                  >
                    £19 per month
                  </Text>
                </Box>
              </Flex>

              <SimpleGrid
                columns={2}
                spacing={3}
              >
                <Metric label="Impressions" />
                <Metric label="QR scans" />
                <Metric label="Scan rate" />
                <Metric label="Slot performance" />
                <Metric label="Scan trends" />
                <Metric label="CSV and PDF reports" />
              </SimpleGrid>
            </Box>

            <Box>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg="white"
                color={BRAND_RED_DARK}
                borderWidth="1px"
                borderColor={BRAND_BORDER}
                fontSize="xs"
                fontWeight="extrabold"
              >
                OPTIONAL PRO UPGRADE
              </Badge>

              <Heading
                mt={4}
                color="gray.900"
                size={{
                  base: "lg",
                  md: "xl",
                }}
              >
                See how your coupons perform
              </Heading>

              <Text
                mt={4}
                color="gray.600"
                lineHeight="1.8"
              >
                Upgrade to Pro for QR analytics, slot-level
                performance, trends and downloadable reports.
              </Text>

              <Text
                mt={3}
                color="gray.600"
                lineHeight="1.8"
              >
                Pro is optional and separate from the coupon-placement
                price.
              </Text>

              <Button
                mt={6}
                size="lg"
                bg={BRAND_RED}
                color="white"
                fontWeight="extrabold"
                _hover={{
                  bg: BRAND_RED_DARK,
                }}
                onClick={() => navigate("/pricing")}
              >
                View Pricing
              </Button>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* =====================================================
          REFERRALS
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg="white"
      >
        <Container maxW="container.md">
          <Box
            p={{
              base: 6,
              md: 10,
            }}
            bg="gray.900"
            color="white"
            borderRadius="2xl"
            boxShadow="xl"
            textAlign="center"
          >
            <Flex
              width="54px"
              height="54px"
              mx="auto"
              align="center"
              justify="center"
              borderRadius="full"
              bg={BRAND_RED}
              fontSize="2xl"
            >
              <FiGift />
            </Flex>

            <Text
              mt={5}
              color="whiteAlpha.800"
              fontSize="xs"
              fontWeight="extrabold"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Business referral credit
            </Text>

            <Heading
              mt={2}
              fontSize={{
                base: "3xl",
                md: "4xl",
              }}
              letterSpacing="-0.03em"
            >
              Give £10. Get £10.
            </Heading>

            <Text
              mt={4}
              maxW="620px"
              mx="auto"
              color="whiteAlpha.900"
              lineHeight="1.8"
            >
              Refer another business to postcode.coupons and, when
              the referral qualifies, both businesses receive £10
              account credit.
            </Text>

            <Text
              mt={2}
              color="whiteAlpha.700"
              fontSize="sm"
            >
              Account credit can be used towards future coupon
              placements.
            </Text>

            <Flex
              direction={{
                base: "column",
                sm: "row",
              }}
              justify="center"
              gap={3}
              mt={6}
            >
              <Button
                size="lg"
                bg={BRAND_RED}
                color="white"
                fontWeight="extrabold"
                _hover={{
                  bg: BRAND_RED_DARK,
                }}
                onClick={() => navigate("/referrals")}
              >
                Refer a Business
              </Button>

              <Button
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.600"
                color="white"
                _hover={{
                  bg: "whiteAlpha.200",
                }}
                onClick={() =>
                  navigate("/signup?business=true")
                }
              >
                Create Business Account
              </Button>
            </Flex>

            <Text
              mt={4}
              color="whiteAlpha.600"
              fontSize="xs"
            >
              A signed-in business account is required to create a
              referral.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg={BRAND_RED_LIGHT}
        borderTopWidth="1px"
        borderColor={BRAND_BORDER}
      >
        <Container maxW="container.md">
          <Box
            mb={{
              base: 7,
              md: 9,
            }}
            textAlign="center"
          >
            <Heading
              color="gray.900"
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              Business FAQ
            </Heading>

            <Text
              mt={3}
              color="gray.600"
              lineHeight="1.7"
            >
              The essentials about pricing, campaign capacity,
              redemption, analytics and referrals.
            </Text>
          </Box>

          <Accordion
            allowToggle
            bg="white"
            borderWidth="1px"
            borderColor={BRAND_BORDER}
            borderRadius="xl"
            overflow="hidden"
          >
            <FaqItem
              question="How much does coupon placement cost?"
            >
              Unless a different price is clearly shown before
              payment, coupon placement costs 1p per household per
              postcode sector. The displayed household count is used
              to calculate the price.
            </FaqItem>

            <FaqItem
              question="What is included with my coupon?"
            >
              Your placement includes your coupon position in the
              selected postcode-sector campaign, an online coupon
              presence and a QR redemption page.
            </FaqItem>

            <FaqItem
              question="When does the campaign print?"
            >
              A campaign proceeds to printing after the required
              campaign capacity has been filled and the required
              payments and coupon content are complete.
            </FaqItem>

            <FaqItem
              question="Will my coupon be moved to another sector?"
            >
              Your paid placement remains in the selected postcode
              sector unless you agree to move it. Postcode.Coupons
              may offer another suitable sector, but the placement is
              not moved automatically.
            </FaqItem>

            <FaqItem
              question="What is the coupon redemption page?"
            >
              It is the mobile-friendly page opened by your coupon's
              QR code. It can display your coupon, business details,
              redemption instructions and applicable terms.
            </FaqItem>

            <FaqItem
              question="Are QR analytics included?"
            >
              QR Analytics Pro is an optional £19 monthly
              subscription. Pro includes impressions, scans, scan
              rate, slot-level performance, trends and downloadable
              reports.
            </FaqItem>

            <FaqItem
              question="How does Give £10, Get £10 work?"
            >
              When a referral qualifies, the referring business and
              the referred business each receive £10 account credit.
              A signed-in business account is required to create and
              complete a referral.
            </FaqItem>

            <FaqItem
              question="Can I cancel for a refund?"
            >
              Business bookings, coupon placements, account credits,
              subscriptions and paid platform features are
              non-refundable once purchased, except where required
              by law. Full details are provided in the Terms of
              Service.
            </FaqItem>

            <FaqItem
              question="Is delivery guaranteed to every address?"
            >
              Royal Mail delivery is subject to Royal Mail's
              operational processes. Postcode.Coupons does not
              guarantee delivery on an exact date or to every
              individual household address in a postcode sector.
            </FaqItem>

            <FaqItem
              question="How do I get started?"
            >
              Create a business account, choose a postcode sector and
              complete the coupon-placement journey.
            </FaqItem>
          </Accordion>
        </Container>
      </Box>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <Box
        as="section"
        py={{
          base: 11,
          md: 16,
        }}
        bg={BRAND_RED}
        color="white"
      >
        <Container maxW="container.md">
          <VStack
            spacing={4}
            textAlign="center"
          >
            <Heading
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              Choose your postcode sector
            </Heading>

            <Text
              maxW="620px"
              opacity={0.94}
              lineHeight="1.7"
            >
              Select the sector, view its household count and place
              your coupon.
            </Text>

            <Flex
              direction={{
                base: "column",
                sm: "row",
              }}
              justify="center"
              width={{
                base: "100%",
                sm: "auto",
              }}
              gap={3}
              pt={2}
            >
              <Button
                size="lg"
                width={{
                  base: "100%",
                  sm: "auto",
                }}
                px={8}
                bg="white"
                color={BRAND_RED_DARK}
                fontWeight="extrabold"
                _hover={{
                  bg: BRAND_RED_LIGHT,
                }}
                onClick={() => navigate("/map")}
              >
                View Postcode Map
              </Button>

              <Button
                size="lg"
                width={{
                  base: "100%",
                  sm: "auto",
                }}
                px={8}
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                fontWeight="extrabold"
                _hover={{
                  bg: "whiteAlpha.200",
                }}
                onClick={() =>
                  navigate("/signup?business=true")
                }
              >
                Create Business Account
              </Button>
            </Flex>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

// -------------------------------------------------------
// FEATURE CARD
// -------------------------------------------------------

function FeatureCard({
  icon: Icon,
  title,
  children,
}) {
  return (
    <Box
      p={{
        base: 5,
        md: 6,
      }}
      bg="white"
      borderWidth="1px"
      borderColor={BRAND_BORDER}
      borderRadius="xl"
      boxShadow="sm"
    >
      <Flex
        width="46px"
        height="46px"
        align="center"
        justify="center"
        borderRadius="full"
        bg={BRAND_RED_LIGHT}
        color={BRAND_RED}
        fontSize="xl"
      >
        <Icon />
      </Flex>

      <Heading
        mt={4}
        size="sm"
        color="gray.900"
      >
        {title}
      </Heading>

      <Text
        mt={2}
        color="gray.600"
        fontSize="sm"
        lineHeight="1.7"
      >
        {children}
      </Text>
    </Box>
  );
}

// -------------------------------------------------------
// PROCESS CARD
// -------------------------------------------------------

function ProcessCard({
  number,
  icon: Icon,
  title,
  children,
}) {
  return (
    <Box
      p={{
        base: 5,
        md: 6,
      }}
      bg="white"
      borderWidth="1px"
      borderColor={BRAND_BORDER}
      borderRadius="xl"
      boxShadow="sm"
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <Flex
          width="42px"
          height="42px"
          align="center"
          justify="center"
          borderRadius="full"
          bg={BRAND_RED_LIGHT}
          color={BRAND_RED}
          fontSize="lg"
        >
          <Icon />
        </Flex>

        <Text
          color={BRAND_RED}
          fontSize="xs"
          fontWeight="extrabold"
          letterSpacing="wide"
        >
          {number}
        </Text>
      </Flex>

      <Heading
        mt={4}
        size="sm"
        color="gray.900"
      >
        {title}
      </Heading>

      <Text
        mt={2}
        color="gray.600"
        fontSize="sm"
        lineHeight="1.7"
      >
        {children}
      </Text>
    </Box>
  );
}

// -------------------------------------------------------
// FEATURE LIST ITEM
// -------------------------------------------------------

function Feature({
  children,
}) {
  return (
    <ListItem
      display="flex"
      alignItems="flex-start"
      gap={2}
    >
      <ListIcon
        as={FiCheck}
        color={BRAND_RED}
        mt="3px"
      />

      <Text lineHeight="1.6">
        {children}
      </Text>
    </ListItem>
  );
}

// -------------------------------------------------------
// ANALYTICS METRIC
// -------------------------------------------------------

function Metric({
  label,
}) {
  return (
    <Flex
      align="center"
      gap={2}
      p={3}
      bg="gray.50"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.100"
    >
      <FiCheck color={BRAND_RED} />

      <Text
        color="gray.700"
        fontSize="sm"
        fontWeight="semibold"
      >
        {label}
      </Text>
    </Flex>
  );
}

// -------------------------------------------------------
// FAQ ITEM
// -------------------------------------------------------

function FaqItem({
  question,
  children,
}) {
  return (
    <AccordionItem
      borderColor={BRAND_BORDER}
      _last={{
        borderBottomWidth: 0,
      }}
    >
      <AccordionButton
        px={{
          base: 4,
          md: 5,
        }}
        py={4}
        _hover={{
          bg: BRAND_RED_LIGHT,
        }}
        _expanded={{
          bg: BRAND_RED_LIGHT,
          color: BRAND_RED_DARK,
        }}
      >
        <Box
          flex="1"
          textAlign="left"
          fontWeight="bold"
        >
          {question}
        </Box>

        <AccordionIcon />
      </AccordionButton>

      <AccordionPanel
        px={{
          base: 4,
          md: 5,
        }}
        pb={5}
        color="gray.600"
        lineHeight="1.8"
      >
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}